import { api } from '../apiClient'
import type { DirectoryCountry, DirectoryCounty, DirectoryTown, DirectoryPostcode, DirectoryNeighbourhood, DirectoryPlumberListResponse, DirectoryPlumberProfile, DirectorySearchResult } from '../../types'

export type DirectorySortBy = 'relevance' | 'rating' | 'reviews' | 'name'
export type DirectoryPlumberPageParams = { skip?: number; limit?: number }
export type DirectoryExtraFilters = {
  hasPhotos?: boolean
  hasRating?: boolean
  emergencyOnly?: boolean
  certification?: string
  sortBy?: DirectorySortBy
}

function directoryParams(emergency?: boolean, gasSafe?: boolean, waterSafe?: boolean) {
  if (!emergency && !gasSafe && !waterSafe) return undefined
  return {
    ...(emergency ? { emergency: 'true' } : {}),
    ...(gasSafe ? { gasSafe: 'true' } : {}),
    ...(waterSafe ? { waterSafe: 'true' } : {}),
  }
}

function directoryListParams(
  emergency?: boolean,
  gasSafe?: boolean,
  waterSafe?: boolean,
  page?: DirectoryPlumberPageParams,
  extra?: DirectoryExtraFilters,
) {
  return {
    ...directoryParams(emergency, gasSafe, waterSafe),
    ...(page?.skip !== undefined ? { skip: page.skip } : {}),
    ...(page?.limit !== undefined ? { limit: page.limit } : {}),
    ...(extra?.hasPhotos     ? { hasPhotos: 'true' }              : {}),
    ...(extra?.hasRating     ? { hasRating: 'true' }              : {}),
    ...(extra?.emergencyOnly ? { emergencyOnly: 'true' }          : {}),
    ...(extra?.certification  ? { certification: extra.certification } : {}),
    ...(extra?.sortBy && extra.sortBy !== 'relevance' ? { sortBy: extra.sortBy } : {}),
  }
}

export async function getDirectoryCountries(emergency?: boolean, gasSafe?: boolean, waterSafe?: boolean): Promise<DirectoryCountry[]> {
  const res = await api.get('/api/directory/countries', { params: directoryParams(emergency, gasSafe, waterSafe) })
  return res.data
}

export async function getDirectoryCounties(countrySlug: string, emergency?: boolean, gasSafe?: boolean, waterSafe?: boolean): Promise<DirectoryCounty[]> {
  const res = await api.get(`/api/directory/${countrySlug}/counties`, { params: directoryParams(emergency, gasSafe, waterSafe) })
  return res.data
}

export async function getDirectoryTowns(countrySlug: string, countySlug: string, emergency?: boolean, gasSafe?: boolean, waterSafe?: boolean): Promise<DirectoryTown[]> {
  const res = await api.get(`/api/directory/${countrySlug}/${countySlug}/towns`, { params: directoryParams(emergency, gasSafe, waterSafe) })
  return res.data
}

export async function getDirectoryPostcodes(countrySlug: string, countySlug: string, townSlug: string, emergency?: boolean, gasSafe?: boolean, waterSafe?: boolean): Promise<DirectoryPostcode[]> {
  const res = await api.get(`/api/directory/${countrySlug}/${countySlug}/${townSlug}/postcodes`, { params: directoryParams(emergency, gasSafe, waterSafe) })
  return res.data
}

export async function getDirectoryNeighbourhoods(countrySlug: string, countySlug: string, emergency?: boolean, gasSafe?: boolean, waterSafe?: boolean): Promise<DirectoryNeighbourhood[]> {
  const res = await api.get(`/api/directory/${countrySlug}/${countySlug}/areas`, { params: directoryParams(emergency, gasSafe, waterSafe) })
  return res.data
}

export async function getDirectoryPlumbersForNeighbourhood(
  countrySlug: string,
  countySlug: string,
  neighbourhoodSlug: string,
  emergency?: boolean,
  gasSafe?: boolean,
  waterSafe?: boolean,
  page?: DirectoryPlumberPageParams,
  extra?: DirectoryExtraFilters,
): Promise<DirectoryPlumberListResponse & { neighbourhood: { slug: string; name: string; postcodeDistricts: string[] } }> {
  const res = await api.get(`/api/directory/${countrySlug}/${countySlug}/areas/${neighbourhoodSlug}/plumbers`, {
    params: directoryListParams(emergency, gasSafe, waterSafe, page, extra),
  })
  return res.data
}

export async function getDirectoryPlumbersForCounty(
  countrySlug: string,
  countySlug: string,
  emergency?: boolean,
  gasSafe?: boolean,
  waterSafe?: boolean,
  page?: DirectoryPlumberPageParams,
  extra?: DirectoryExtraFilters,
): Promise<DirectoryPlumberListResponse> {
  const res = await api.get(`/api/directory/${countrySlug}/${countySlug}/plumbers`, {
    params: directoryListParams(emergency, gasSafe, waterSafe, page, extra),
  })
  return res.data
}

export async function getDirectoryPlumbersForTown(
  countrySlug: string,
  countySlug: string,
  townSlug: string,
  emergency?: boolean,
  gasSafe?: boolean,
  waterSafe?: boolean,
  page?: DirectoryPlumberPageParams,
  extra?: DirectoryExtraFilters,
): Promise<DirectoryPlumberListResponse> {
  const res = await api.get(`/api/directory/${countrySlug}/${countySlug}/${townSlug}/plumbers`, {
    params: directoryListParams(emergency, gasSafe, waterSafe, page, extra),
  })
  return res.data
}

export async function getDirectoryPlumbers(
  countrySlug: string,
  countySlug: string,
  townSlug: string,
  postcodeSlug: string,
  emergency?: boolean,
  gasSafe?: boolean,
  waterSafe?: boolean,
  page?: DirectoryPlumberPageParams,
  extra?: DirectoryExtraFilters,
): Promise<DirectoryPlumberListResponse> {
  const res = await api.get(`/api/directory/${countrySlug}/${countySlug}/${townSlug}/${postcodeSlug}/plumbers`, {
    params: directoryListParams(emergency, gasSafe, waterSafe, page, extra),
  })
  return res.data
}

export async function getDirectoryPlumberBySlug(directorySlug: string): Promise<DirectoryPlumberProfile> {
  const res = await api.get(`/api/directory/find/${directorySlug}`)
  return res.data
}

export async function getDirectoryPlumber(
  countrySlug: string,
  countySlug: string,
  townSlug: string,
  postcodeSlug: string,
  directorySlug: string,
  emergency?: boolean,
  gasSafe?: boolean,
  waterSafe?: boolean,
): Promise<DirectoryPlumberProfile> {
  const res = await api.get(
    `/api/directory/${countrySlug}/${countySlug}/${townSlug}/${postcodeSlug}/${directorySlug}`,
    { params: directoryParams(emergency, gasSafe, waterSafe) },
  )
  return res.data
}


export async function searchDirectory(q: string): Promise<DirectorySearchResult[]> {
  const res = await api.get('/api/directory/search', { params: { q } })
  return res.data
}
